import type { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import type { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import type { NumberFilter } from 'v5/api/_common/query/filters/NumberFilter';
import type { StringFilter } from 'v5/api/_common/query/filters/StringFilter';
import {
  resourceQueryUtils,
  type ResourceSearchQuery
} from 'v5/api/resource/resource.api';
import type { CustomFieldAnyFilter } from 'v5/core/custom-fields/custom-fields-filters.types';
import { isRecord } from 'v5/platform/lang/isRecord';

/**
 * A part of a role's saved search that the v5 search can't express: shown to the user, never
 * dropped. `operator` is the v1 one (`gte`, `s_with`, `availability`, `sort`…).
 */
interface NotAppliedPart {
  field: string;
  operator: string;
  value: unknown;
}

export interface ResourceSearchOfV1 {
  query: ResourceSearchQuery;
  notApplied: NotAppliedPart[];
}

type Converted =
  | {
      type: 'applied';
      apply: (query: ResourceSearchQuery) => ResourceSearchQuery;
    }
  | { type: 'notApplied'; parts: NotAppliedPart[] };

const numberOf = (value: unknown): number | null => {
  const number = typeof value === 'string' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
};

const stringsOf = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every(item => typeof item === 'string')
    ? value
    : null;

const numbersOf = (value: unknown): number[] | null => {
  const numbers = Array.isArray(value) ? value.map(numberOf) : null;
  return numbers?.every(number => number !== null)
    ? numbers.filter(number => number !== null)
    : null;
};

/**
 * The server reads these off a filter, or (`groups` inside a custom field's filter, copied
 * there by the v1 form) rejects them as an unknown operator: no part of the search either way.
 */
const NOT_OPERATORS = new Set([
  'type',
  'dataType',
  'id',
  'custom_field_definition_type',
  'groups'
]);

/** A filter's operators, `$` dropped: the server reads `eq` as `$eq` (SearchRouter handlers). */
const operatorsOf = (filter: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(filter)
      .filter(([key, value]) => !NOT_OPERATORS.has(key) && value !== undefined)
      .map(([key, value]) => [key.replace(/^\$/, ''), value])
  );

const notApplied = (field: string, filter: unknown): Converted => ({
  type: 'notApplied',
  parts: isRecord(filter)
    ? Object.entries(operatorsOf(filter)).map(([operator, value]) => ({
        field,
        operator,
        value
      }))
    : [{ field, operator: 'eq', value: filter }]
});

// derived from the role, not a user's edit: the page stays (it is the first anyway)
const keepPage = { preservePage: true };

/**
 * Array fields (tags, groups, a multi-select): a bare array is `$eq` of the array, any of them,
 * as v5's `matchAny` sends it; `match` is all of them (`$match`); `any` is any of them (`$any`,
 * the same rows as `$eq` of an array on the index and on the source tables).
 */
function arrayFilter<V>(
  operators: Record<string, unknown>,
  valuesOf: (value: unknown) => V[] | null
): EnumFilter<V> | null {
  const entries = Object.entries(operators);
  if (entries.length !== 1) return null;
  const [operator, raw] = entries[0];
  const values = valuesOf(raw);
  if (!values?.length) return null;
  switch (operator) {
    case 'eq':
    case 'any':
      return { operator: 'matchAny', value: values };
    case 'match':
      return { operator: 'matchAll', value: values };
    case 'nmatch':
      return { operator: 'matchNone', value: values };
    default:
      return null;
  }
}

/** `eq`, `ne`, `gt`, `lt`, `gte` and `lte` one to one; `gte` with `lte` is `between`. */
function numberFilter(operators: Record<string, unknown>): NumberFilter | null {
  const keys = Object.keys(operators).sort().join(',');
  const value = (key: string) => numberOf(operators[key]);
  const one = (
    operator: Exclude<NumberFilter['operator'], 'between'>,
    key: string
  ): NumberFilter | null => {
    const number = value(key);
    return number === null ? null : { operator, value: number };
  };
  switch (keys) {
    case 'eq':
      return one('equal', 'eq');
    case 'ne':
      return one('notEqual', 'ne');
    case 'gt':
      return one('greaterThan', 'gt');
    case 'lt':
      return one('lessThan', 'lt');
    case 'gte':
      return one('greaterOrEqual', 'gte');
    case 'lte':
      return one('lessOrEqual', 'lte');
    case 'gte,lte': {
      const from = value('gte');
      const to = value('lte');
      return from === null || to === null
        ? null
        : { operator: 'between', value: { from, to } };
    }
    default:
      return null;
  }
}

/** Only `eq` (`$eq`): v1's `s_with` / `e_with` are no server operator, so never searched. */
function stringFilter(operators: Record<string, unknown>): StringFilter | null {
  const keys = Object.keys(operators);
  return keys.length === 1 && typeof operators.eq === 'string'
    ? { operator: 'equals', value: operators.eq }
    : null;
}

function booleanFilter(
  operators: Record<string, unknown>
): BooleanFilter | null {
  const keys = Object.keys(operators);
  return keys.length === 1 && typeof operators.eq === 'boolean'
    ? { operator: 'equal', value: operators.eq }
    : null;
}

/** A single select is one value: `$eq` of it is `$in` of it alone, as v5's `matchAny` sends it. */
function singleSelectFilter(
  operators: Record<string, unknown>
): EnumFilter<string> | null {
  const keys = Object.keys(operators);
  const raw = operators.eq;
  const value = Array.isArray(raw) && raw.length === 1 ? raw[0] : raw;
  return keys.length === 1 && typeof value === 'string'
    ? { operator: 'matchAny', value: [value] }
    : null;
}

const tagNamesOf = (value: unknown): string[] | null =>
  Array.isArray(value)
    ? stringsOf(value.map(item => (isRecord(item) ? item.tag : item)))
    : null;

const CUSTOM_FIELD_FILTERS: Record<
  string,
  (operators: Record<string, unknown>) => CustomFieldAnyFilter | null
> = {
  SHORT_TEXT: stringFilter,
  LONG_TEXT: stringFilter,
  URL: stringFilter,
  NUMBER: numberFilter,
  PERCENTAGE: numberFilter,
  CURRENCY: numberFilter,
  MEASUREMENT: numberFilter,
  MEASUREMENT_NEW: numberFilter,
  HEIGHT: numberFilter,
  BOOLEAN: booleanFilter,
  SINGLE_SELECT: singleSelectFilter,
  MULTI_SELECT: operators => arrayFilter(operators, stringsOf)
};

const STRING_CORE_FIELDS = [
  'role',
  'emails',
  'phone_numbers',
  'websites',
  'socials',
  'locations'
] as const;

const isStringCoreField = (
  field: string
): field is (typeof STRING_CORE_FIELDS)[number] =>
  STRING_CORE_FIELDS.some(core => core === field);

/** The v5 filter that sends what the v1 one sends, set on a query; null when there is none. */
function applierOf(
  field: string,
  filter: Record<string, unknown>
): Extract<Converted, { type: 'applied' }>['apply'] | null {
  const operators = operatorsOf(filter);
  if (field === 'resource_type_id') {
    // a scalar: `$eq` of an id is `$in` of it alone, as v5's `matchAny` sends it
    const id =
      Object.keys(operators).length === 1 ? numberOf(operators.eq) : null;
    return id === null
      ? null
      : query =>
          resourceQueryUtils.setFilter(
            query,
            'resource_type_id',
            { operator: 'matchAny', value: [id] },
            keepPage
          );
  }
  if (field === 'groups') {
    const groups = arrayFilter(operators, numbersOf);
    return groups
      ? query => resourceQueryUtils.setFilter(query, 'groups', groups, keepPage)
      : null;
  }
  if (field === 'tags') {
    const tags = arrayFilter(operators, tagNamesOf);
    return tags
      ? query => resourceQueryUtils.setFilter(query, 'tags', tags, keepPage)
      : null;
  }
  if (isStringCoreField(field)) {
    const text = stringFilter(operators);
    return text
      ? query => resourceQueryUtils.setFilter(query, field, text, keepPage)
      : null;
  }
  if (/^custom_\d+$/.test(field)) {
    const type = filter.custom_field_definition_type;
    const custom =
      typeof type === 'string' ? CUSTOM_FIELD_FILTERS[type]?.(operators) : null;
    return custom
      ? query => resourceQueryUtils.setFilter(query, field, custom, keepPage)
      : null;
  }
  // `availability` is v1's "has a booking in these statuses"; v5's Available is the opposite
  return null;
}

function convertFilter(field: string, filter: unknown): Converted {
  const asRecord = Array.isArray(filter) ? { eq: filter } : filter;
  const apply = isRecord(asRecord) ? applierOf(field, asRecord) : null;
  return apply ? { type: 'applied', apply } : notApplied(field, asRecord);
}

const SORT_FIELDS: Record<string, 'full_name' | 'role' | 'createdAt'> = {
  full_name: 'full_name',
  role: 'role',
  created_at: 'createdAt'
};

/**
 * A v1 search (the `/search/contacts` body, as a role or a calendar saves it) as a v5 search, each v1 operator turned
 * into the v5 filter that sends the same one; what has no such filter comes back in
 * `notApplied` rather than being dropped or approximated.
 */
export function resourceSearchOfV1(v1Search: unknown): ResourceSearchOfV1 {
  const base = resourceQueryUtils.ofDefault();
  const source = isRecord(v1Search) ? v1Search : {};
  const converted = Object.entries(
    isRecord(source.filters) ? source.filters : {}
  ).map(([field, filter]) => convertFilter(field, filter));
  const order = isRecord(source.order_by) ? source.order_by : null;
  const sortField =
    typeof order?.field === 'string' ? SORT_FIELDS[order.field] : undefined;
  const query = converted.reduce(
    (query, result) =>
      result.type === 'applied' ? result.apply(query) : query,
    {
      ...base,
      search: typeof source.query === 'string' ? source.query : '',
      sort: sortField
        ? {
            field: sortField,
            order: /^desc$/i.test(String(order?.direction))
              ? ('desc' as const)
              : ('asc' as const)
          }
        : base.sort
    }
  );
  return {
    query,
    notApplied: [
      ...converted.flatMap(result =>
        result.type === 'notApplied' ? result.parts : []
      ),
      ...(order && !sortField
        ? [{ field: 'order_by', operator: 'sort', value: order.field }]
        : [])
    ]
  };
}
